import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { TranslateFormatText } from '../../helpers/array.helpers';
import { TranslateHelper } from '../../helpers/injector.helper';
import { KIDefinitionService } from '../../services/kidefinition.service';
import { BaseListDetail, BaseListSettings, DeleteTexts, SaveTexts } from '../base.list.settings';

const CAPABILITY_FLAGS = [
    { value: 1, label: '@@Chat' },
    { value: 2, label: '@@Embedding' },
    { value: 4, label: '@@VoiceToText' },
    { value: 8, label: '@@VectorQuery' },
    { value: 16, label: '@@Ocr' }
];

@Component({
    selector: 'ki-settings',
    templateUrl: '../base.list.settings.html'
})
export class KISettings extends BaseListSettings {

    static GetSettingsEntry() {
        return {
            Caption: '@@KI-Anbindungen',
            ID: 'kidefinition',
            Icon: 'sparkles',
            Index: 25,
            Parent: 'settings',
            Security: {
                Name: 'evidanza.App.Shared.Security.PageSettingsRight',
                Value: 8
            },
            Content: KISettings
        };
    }

    constructor(protected cdRef: ChangeDetectorRef, private service: KIDefinitionService) {
        super(cdRef);
    }

    getContentType() {
        return KIDetail;
    }

    loadList(handler) {
        this.service.GetKIDefinitions().subscribe(result => {
            if (result) {
                const list = [];
                result.forEach(entry => {
                    list.push({
                        Caption: entry.Caption,
                        ID: entry.SID,
                        IsCapsule: entry.IsCapsule,
                        IsOverridden: entry.IsOverridden
                    });
                });
                handler(list);
            }
        });
    }

    loadData(data) {
        this.service.LoadKIDefinition(data).subscribe(result => {
            if (result) {
                this.setSelectedItem(result);
            }
        });
    }

    getNewItem() {
        return {
            Name: TranslateHelper.TranslatorInstance.instant('@@Neue KI-Anbindung'),
            APIKey: '',
            UrlEndPoint: '',
            MCPServers: [],
            Models: []
        };
    }

    getDeleteText(sel): DeleteTexts {
        const retVal = new DeleteTexts();
        retVal.Question = new TranslateFormatText('@@Sind Sie sicher, dass Sie die KI-Anbindung \'{0}\' loeschen moechten?');
        retVal.Question.FormatParams.push(sel.Caption);
        retVal.Success = new TranslateFormatText('@@KI-Anbindung \'{0}\' erfolgreich geloescht.');
        retVal.Success.FormatParams.push(sel.Caption);
        retVal.Title = new TranslateFormatText('@@KI-Anbindung loeschen');
        return retVal;
    }

    getSaveSuccessText(sel): SaveTexts {
        const retVal = new SaveTexts();
        retVal.Text = new TranslateFormatText('@@KI-Anbindung \'{0}\' erfolgreich gespeichert.');
        retVal.Text.FormatParams.push(sel.Name);
        retVal.Title = new TranslateFormatText('@@KI-Anbindung speichern');
        return retVal;
    }

    delete(data, handler) {
        this.service.DeleteKIDefinition(data).subscribe(res => handler(res));
    }

    saveInternal(item, handler) {
        this.service.SaveKIDefinition(item).subscribe(result => {
            if (result) {
                handler(result, result.SID, result.Caption);
            }
        });
    }

    handleNew(item, result) {
        item.SID = result.SID;
        item.Version = result.Version;
    }

    updateListItem(item, result) {
        item.IsCapsule = result.IsCapsule;
        item.IsOverridden = result.IsOverridden;
    }
}

@Component({
    selector: 'ki-detail',
    templateUrl: './ki.settings.html',
    styleUrls: ['./ki.settings.css']
})
export class KIDetail extends BaseListDetail implements OnInit {

    private static readonly GA_KI_TYPE_ID = 'f1d8c3a6-7b2e-4a91-9c5d-8e4f6a0b1d2c';

    KITypes: any[] = [];
    AvailableModels: any[] = [];
    LoadingModels = false;
    CapabilityFlags = CAPABILITY_FLAGS;

    get isGaKiType(): boolean {
        return this.SelectedItem?.KITypeId?.toLowerCase() === KIDetail.GA_KI_TYPE_ID;
    }

    constructor(private service: KIDefinitionService) {
        super();
    }

    ngOnInit() {
        super.ngOnInit();
        this.service.GetKITypes().subscribe(types => this.KITypes = types || []);
    }

    reloadModels() {
        if (!this.SelectedItem || !this.SelectedItem.SID) {
            return;
        }
        this.LoadingModels = true;
        this.service.GetKIModels(this.SelectedItem.SID).subscribe(
            models => {
                this.AvailableModels = models || [];
                this.LoadingModels = false;
            },
            _ => {
                this.LoadingModels = false;
            });
    }

    addModelFromList(available: any) {
        if (!available) return;
        if (!this.SelectedItem.Models) this.SelectedItem.Models = [];
        if (!this.SelectedItem.Models.find(m => m.Name === available.Name)) {
            this.SelectedItem.Models.push({
                Name: available.Name,
                Caption: available.Caption || '',
                ContextLength: available.ContextLength || 0,
                Capabilities: 1
            });
            this.OnItemChanged();
        }
    }

    onModelAutoSelected(model: any, selectedName: string) {
        const found = this.AvailableModels.find(am => am.Name === selectedName);
        if (found) {
            model.Caption = found.Caption || '';
            model.ContextLength = found.ContextLength || 0;
        }
        this.OnItemChanged();
    }

    addModel() {
        if (!this.SelectedItem.Models) {
            this.SelectedItem.Models = [];
        }
        this.SelectedItem.Models.push({ Name: '', Caption: '', ContextLength: 0, Capabilities: 1 });
        this.OnItemChanged();
    }

    removeModel(model) {
        const i = this.SelectedItem.Models.indexOf(model);
        if (i >= 0) {
            this.SelectedItem.Models.splice(i, 1);
            this.OnItemChanged();
        }
    }

    hasCapability(model, flag: number): boolean {
        return ((model.Capabilities || 0) & flag) !== 0;
    }

    toggleCapability(model, flag: number) {
        const current = model.Capabilities || 0;
        model.Capabilities = (current & flag) !== 0 ? current & ~flag : current | flag;
        this.OnItemChanged();
    }

    addMCPServer() {
        if (!this.SelectedItem.MCPServers) {
            this.SelectedItem.MCPServers = [];
        }
        this.SelectedItem.MCPServers.push({
            Id: KIDetail.newGuid(),
            Name: '',
            URL: '',
            AuthorizatenHeader: '',
            Internal: false
        });
        this.OnItemChanged();
    }

    removeMCPServer(server) {
        const i = this.SelectedItem.MCPServers.indexOf(server);
        if (i >= 0) {
            this.SelectedItem.MCPServers.splice(i, 1);
            this.OnItemChanged();
        }
    }

    private static newGuid(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = Math.random() * 16 | 0;
            return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
    }
}
